<script setup lang="ts">
import { ref, reactive } from 'vue';
import { Loader2, Send, RotateCcw, Swords, Scroll, Sparkles } from 'lucide-vue-next';

// 胶水集成：使用 shadcn-vue 组件构建表单 UI
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';

import SchoolSelector from './components/SchoolSelector.vue';
import SceneSelector from './components/SceneSelector.vue';
import TalentCodeInput from './components/TalentCodeInput.vue';
import TalentDescription from './components/TalentDescription.vue';
import UserIdInput from './components/UserIdInput.vue';
import { schoolsData, getXinfaBySchoolId } from './data/schools';

/**
 * 剑网3 武学研习奇穴推荐收集表
 * @description 用于收集用户提交的奇穴推荐，包括心法、奇穴、使用简述等
 */

interface FormData {
  school: {
    schoolId: string;
    xinfaId: string;
  };
  scene: string;
  talentCode: string;
  description: string;
  uid: string;
}

const formData = reactive<FormData>({
  school: {
    schoolId: '',
    xinfaId: ''
  },
  scene: '',
  talentCode: '',
  description: '',
  uid: ''
});

const isSubmitting = ref(false);
const submitStatus = ref<'idle' | 'success' | 'error'>('idle');
const submitMessage = ref('');

const validateForm = (): boolean => {
  if (!formData.school.schoolId || !formData.school.xinfaId) {
    submitMessage.value = '请选择门派和心法';
    return false;
  }
  if (!formData.scene) {
    submitMessage.value = '请选择奇穴使用场景';
    return false;
  }
  if (!formData.talentCode.trim()) {
    submitMessage.value = '请输入奇穴编码';
    return false;
  }
  try {
    const parsed = JSON.parse(formData.talentCode);
    if (!Array.isArray(parsed) || parsed.length === 0) {
      submitMessage.value = '奇穴编码格式不正确';
      return false;
    }
  } catch {
    submitMessage.value = '奇穴编码必须是有效的JSON格式';
    return false;
  }
  if (!formData.description.trim() || formData.description.trim().length < 10) {
    submitMessage.value = '请详细描述奇穴使用方法（至少10个字）';
    return false;
  }
  return true;
};

// 使用环境变量，开发时默认为 localhost，构建时使用生产地址
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:9264';

const handleSubmit = async () => {
  if (!validateForm()) {
    submitStatus.value = 'error';
    return;
  }

  isSubmitting.value = true;
  submitStatus.value = 'idle';

  try {
    // 获取门派和心法名称
    const school = schoolsData.find(s => s.id === formData.school.schoolId);
    const xinfaList = getXinfaBySchoolId(formData.school.schoolId);
    const xinfa = xinfaList.find(x => x.id === formData.school.xinfaId);

    // 胶水集成：使用 fetch API 发送数据到后端
    const response = await fetch(`${API_BASE_URL}/api/jx3/talent/submit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        school: {
          schoolId: formData.school.schoolId,
          xinfaId: formData.school.xinfaId,
          schoolName: school?.name || '',
          xinfaName: xinfa?.name || ''
        },
        scene: formData.scene,
        talentCode: formData.talentCode,
        description: formData.description,
        uid: formData.uid || undefined,
        submitTime: new Date().toISOString()
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (result.success) {
      submitStatus.value = 'success';
      submitMessage.value = '提交成功！感谢您的奇穴推荐分享。';
      
      // 重置表单
      setTimeout(() => {
        resetForm();
      }, 2000);
    } else {
      throw new Error(result.message || '提交失败');
    }
  } catch (error) {
    console.error('提交失败:', error);
    submitStatus.value = 'error';
    submitMessage.value = '提交失败，请稍后重试';
  } finally {
    isSubmitting.value = false;
  }
};

const resetForm = () => {
  formData.school = { schoolId: '', xinfaId: '' };
  formData.scene = '';
  formData.talentCode = '';
  formData.description = '';
  formData.uid = '';
  submitStatus.value = 'idle';
  submitMessage.value = '';
};
</script>

<template>
  <div class="min-h-screen bg-background p-4 sm:p-6 lg:p-8">
    <div class="max-w-3xl mx-auto animate-slide-in">
      <!-- 头部卡片 -->
      <Card class="mb-6 border-border/50 shadow-xl overflow-hidden bg-card">
        <CardHeader class="relative bg-secondary/50 pb-8">
          <div class="relative z-10">
            <div class="flex items-center gap-3 mb-2">
              <div class="p-2 bg-primary/10 rounded-lg">
                <Swords class="w-6 h-6 text-primary" />
              </div>
              <CardTitle class="text-2xl sm:text-3xl font-bold">
                武学研习奇穴推荐收集表
              </CardTitle>
            </div>
            <CardDescription class="text-muted-foreground text-base mt-2">
              用于收集用户提交的奇穴推荐，包括心法、奇穴、使用简述等
            </CardDescription>
          </div>
        </CardHeader>
      </Card>

      <!-- 表单内容 -->
      <div class="space-y-4">
        <!-- 01 选择门派/心法 -->
        <Card class="border-border/50 shadow-lg hover:shadow-xl transition-all duration-300 bg-card">
          <CardHeader class="pb-4">
            <div class="flex items-center gap-3">
              <Badge variant="default" class="bg-primary text-primary-foreground font-bold">
                01
              </Badge>
              <CardTitle class="text-lg">选择门派/心法</CardTitle>
              <Badge variant="destructive" class="text-xs">必填</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <SchoolSelector v-model="formData.school" />
          </CardContent>
        </Card>

        <!-- 02 奇穴使用场景 -->
        <Card class="border-border/50 shadow-lg hover:shadow-xl transition-all duration-300 bg-card">
          <CardHeader class="pb-4">
            <div class="flex items-center gap-3">
              <Badge variant="default" class="bg-primary text-primary-foreground font-bold">
                02
              </Badge>
              <CardTitle class="text-lg">奇穴使用场景</CardTitle>
              <Badge variant="destructive" class="text-xs">必填</Badge>
            </div>
            <CardDescription>
              战场、绝境战场（吃鸡）、竞技场、其他，四选一
            </CardDescription>
          </CardHeader>
          <CardContent>
            <SceneSelector v-model="formData.scene" />
          </CardContent>
        </Card>

        <!-- 03 奇穴编码 -->
        <Card class="border-border/50 shadow-lg hover:shadow-xl transition-all duration-300 bg-card">
          <CardHeader class="pb-4">
            <div class="flex items-center gap-3">
              <Badge variant="default" class="bg-primary text-primary-foreground font-bold">
                03
              </Badge>
              <CardTitle class="text-lg">奇穴编码</CardTitle>
              <Badge variant="destructive" class="text-xs">必填</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <TalentCodeInput v-model="formData.talentCode" />
          </CardContent>
        </Card>

        <!-- 04 奇穴使用方法 -->
        <Card class="border-border/50 shadow-lg hover:shadow-xl transition-all duration-300 bg-card">
          <CardHeader class="pb-4">
            <div class="flex items-center gap-3">
              <Badge variant="default" class="bg-primary text-primary-foreground font-bold">
                04
              </Badge>
              <CardTitle class="text-lg">奇穴使用方法</CardTitle>
              <Badge variant="destructive" class="text-xs">必填</Badge>
            </div>
            <CardDescription>
              请简述这套奇穴的打法思路
            </CardDescription>
          </CardHeader>
          <CardContent>
            <TalentDescription v-model="formData.description" />
          </CardContent>
        </Card>

        <!-- 05 魔盒UID（可选） -->
        <Card class="border-border/50 shadow-lg hover:shadow-xl transition-all duration-300 bg-card">
          <CardHeader class="pb-4">
            <div class="flex items-center gap-3">
              <Badge variant="default" class="bg-primary text-primary-foreground font-bold">
                05
              </Badge>
              <CardTitle class="text-lg">魔盒UID</CardTitle>
              <Badge variant="secondary" class="text-xs">可选</Badge>
            </div>
            <CardDescription>
              填写后可标识您的作者身份
            </CardDescription>
          </CardHeader>
          <CardContent>
            <UserIdInput v-model="formData.uid" />
          </CardContent>
        </Card>
      </div>

      <!-- 提交区域 -->
      <Card class="mt-6 border-border/50 shadow-xl bg-card">
        <CardContent class="pt-6">
          <!-- 状态提示 -->
          <Alert 
            v-if="submitStatus !== 'idle'" 
            :variant="submitStatus === 'error' ? 'destructive' : 'default'"
            class="mb-4 animate-fade-in"
          >
            <AlertDescription class="flex items-center gap-2">
              <Sparkles v-if="submitStatus === 'success'" class="w-4 h-4" />
              {{ submitMessage }}
            </AlertDescription>
          </Alert>
          
          <!-- 操作按钮 -->
          <div class="flex flex-col sm:flex-row gap-3 justify-center">
            <Button 
              size="lg"
              class="min-w-[160px] gap-2 bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5"
              :disabled="isSubmitting"
              @click="handleSubmit"
            >
              <Loader2 v-if="isSubmitting" class="w-4 h-4 animate-spin" />
              <Send v-else class="w-4 h-4" />
              {{ isSubmitting ? '提交中...' : '提交奇穴推荐' }}
            </Button>
            
            <Button 
              variant="outline"
              size="lg"
              class="min-w-[120px] gap-2 border-border hover:bg-secondary transition-all duration-200"
              :disabled="isSubmitting"
              @click="resetForm"
            >
              <RotateCcw class="w-4 h-4" />
              重置表单
            </Button>
          </div>
        </CardContent>
      </Card>

      <!-- 页脚 -->
      <div class="mt-8 text-center text-muted-foreground text-sm">
        <p class="flex items-center justify-center gap-2">
          <Scroll class="w-4 h-4" />
          剑网3 武学研习 · 奇穴推荐收集
        </p>
      </div>
    </div>
  </div>
</template>
